import { ZodIssue, ZodSchema } from 'zod';
import {
  type QualificationReqPayload,
  type QualificationResponse,
  QualificationResponseSchema,
  type PhoneValidationReqPayload,
  type ValidatePhoneResponse,
  ValidatePhoneResponseSchema,
  type ZipValidationReqPayload,
  type ValidateZipResponse,
  ValidateZipResponseSchema,
  type DiffyReqPayload,
  type DiffyResponse,
  DiffyResponseSchema,
  type LeadRequest,
  type LeadSubmitV4Request,
  type LeadSubmitV5Request,
  type LeadSubmitV5Response,
  LeadSubmitV5ResponseSchema,
  LeadSubmitV5ErrorBodySchema,
  type LeadSubmitResponse,
  LeadSubmitResponseSchema,
  type LeadJsonSchemaResponse,
  LeadJsonSchemaResponseSchema,
  LeadJsonSchemaV4ResponseSchema,
} from './deliverySystemSchema';

const DEFAULT_X_API_KEY = 'voyager';
const LDS_ERROR_CODE = /^[a-z][a-z0-9_]{0,63}$/;

export interface APIRequestOptions extends RequestInit {
  cacheEnabled?: boolean;
  bustCache?: boolean;
}

export interface APIClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  cache?: Map<string, Response>;
  newRelic?: {
    noticeError?: (error: Error, context: Record<string, any>) => void;
  };
  strictValidation?: boolean;
}

/** `message` never carries the upstream body, which can echo lead fields. */
export class LeadSubmitV5Error extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    public readonly errors: unknown[],
  ) {
    super(`LDS v5 lead submit failed: ${status}`);
    this.name = 'LeadSubmitV5Error';
    Object.setPrototypeOf(this, LeadSubmitV5Error.prototype);
  }
}

export class DeliverySystemApiClient {
  /**
   * The cache for the API client.
   */
  public cache: Map<string, Response>;

  /**
   * Constructor for the DeliverySystemApiClient.
   * @param config - The configuration for the API client.
   */
  constructor(public readonly config: APIClientConfig) {
    const defaultConfig: APIClientConfig = {
      baseUrl: '',
      defaultHeaders: {
        'Content-Type': 'application/json',
        'x-api-key': DEFAULT_X_API_KEY,
      },
      cache: new Map(),
      newRelic: {
        noticeError: (error: Error, context: Record<string, any>) => {
          console.error(
            'Error in DeliverySystemApiClient: noticeError',
            error,
            context,
          );
        },
      },
      strictValidation: false,
    };

    const mergedConfig = { ...defaultConfig, ...config };

    this.config = {
      ...mergedConfig,
      defaultHeaders: {
        ...defaultConfig.defaultHeaders,
        ...(mergedConfig.defaultHeaders ?? {}),
      },
    };

    this.cache = this.config.cache ?? new Map();
  }

  /**
   * Returns the name of the API.
   */
  public getApiName(): string {
    return 'delivery-system';
  }

  /**
   * Makes a GET request to the API.
   */
  public async get(
    path: string,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const cacheKey = `${this.config.baseUrl}${path}`;

    // Bust cache if bustCache is provided
    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      return this.cache.get(cacheKey)!.clone();
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...options.headers,
      },
    });
    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }
    return response;
  }

  public async post<Body>(
    path: string,
    body: Body,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const cacheKey = `${this.config.baseUrl}${path}`;

    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      return this.cache.get(cacheKey)!.clone();
    }

    const reqBody =
      body instanceof URLSearchParams ? body : JSON.stringify(body);

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'POST',
      body: reqBody,
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...(options.headers as Record<string, string>),
      },
    });

    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }

    return response;
  }

  private bustCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  private shouldUseCache(
    cacheKey: string,
    options: APIRequestOptions,
  ): boolean {
    if (!options.cacheEnabled) {
      return false;
    }

    return this.cache.has(cacheKey);
  }

  /**
   * Checks if the response has an error.
   */
  public hasError(response: Response): boolean {
    return response.status >= 400;
  }

  /**
   * Converts the response to a JSON object that is defined by the passed in schema.
   */
  public async toJson<T>(response: Response, schema: ZodSchema<T>): Promise<T> {
    const data = await response.json();

    // Validate the full response as-is
    const validated = schema.safeParse(data);

    if (!validated.success) {
      const error = new Error(`Invalid response for ${this.getApiName()}`);
      console.error(error);
      console.log(this.buildErrorMessage(validated.error.errors));

      if (this.config.strictValidation) {
        throw error;
      }

      return data as T;
    }

    return validated.data;
  }

  /**
   * Reports an error to New Relic.
   */
  public reportError(error: Error, context?: Record<string, any>): void {
    if (this.config.newRelic?.noticeError) {
      this.config.newRelic.noticeError(error, context || {});
    }
  }

  /**
   * Builds an error message from the passed in errors.
   */
  private buildErrorMessage(errors: ZodIssue[]): string[] {
    return errors.map(
      (error) => `Path: ${error.path.join('.')}, Message: ${error.message}`,
    );
  }

  // ─── Delivery System Methods ────────────────────────────────────────

  /**
   * Get the lead schema for a specific school and program.
   * @param params - The school ID and program ID.
   * @param options - Optional request options.
   * @returns The lead schema for the school and program (JSON Schema format).
   */
  async getLeadSchema(
    params: { schoolId: string; programId: string },
    options: APIRequestOptions = {},
  ): Promise<LeadJsonSchemaResponse> {
    const response = await this.get(
      `/v3/schema/${params.schoolId}/${params.programId}`,
      options,
    );

    if (this.hasError(response)) {
      console.error(`Failed to get lead schema`);
    }

    const data = await this.toJson(response, LeadJsonSchemaResponseSchema);
    return data;
  }

  /**
   * Get the v4 lead schema for a specific organization and offering.
   * @param params - The organization ID and offering UUID.
   * @param options - Optional request options.
   * @returns The effective lead schema for the organization/offering.
   */
  async getLeadSchemaV4(
    params: { organizationId: string; offeringUuid?: string },
    options: APIRequestOptions = {},
  ): Promise<LeadJsonSchemaResponse> {
    const path = this.buildLeadSchemaV4Path(params);
    const response = await this.get(path, options);

    if (this.hasError(response)) {
      console.error(`Failed to get v4 lead schema`);
      const errorMessage = await this.buildHttpErrorMessage(
        response,
        'Error getting v4 lead schema',
      );

      throw new Error(errorMessage);
    }

    const data = await this.toJson(response, LeadJsonSchemaV4ResponseSchema);
    return data.schema;
  }

  /**
   * Post the lead schema for a specific school and program.
   * @param params - The school ID and program ID.
   * @param body - The qualification request payload.
   * @param options - Optional request options.
   * @returns The response from posting the lead schema (JSON Schema format).
   */
  async postLeadSchema(
    params: { schoolId: string; programId: string },
    body: QualificationReqPayload,
    options: APIRequestOptions = {},
  ): Promise<LeadJsonSchemaResponse> {
    const response = await this.post(
      `/v3/schema/${params.schoolId}/${params.programId}`,
      body,
      options,
    );

    if (this.hasError(response)) {
      console.error(`Failed to post lead schema`);
    }

    const data = await this.toJson(response, LeadJsonSchemaResponseSchema);
    return data;
  }

  /**
   * Qualify a lead using v4 endpoint.
   * @param body - The qualification request payload.
   * @param headers - Optional additional headers.
   * @param options - Optional request options.
   * @returns The qualification response.
   */
  async postLeadQualify(
    body: QualificationReqPayload,
    headers?: Record<string, string>,
    options: APIRequestOptions = {},
  ): Promise<QualificationResponse> {
    const response = await this.post(`/v4/request/qualify`, body, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(`Failed to qualify lead`);
    }

    const data = await this.toJson(response, QualificationResponseSchema);
    return data;
  }

  /**
   * Qualify a lead using v3 endpoint.
   * @param body - The qualification request payload.
   * @param headers - Optional additional headers.
   * @param options - Optional request options.
   * @returns The qualification response.
   */
  async postLeadQualifications(
    body: QualificationReqPayload,
    headers?: Record<string, string>,
    options: APIRequestOptions = {},
  ): Promise<QualificationResponse> {
    const response = await this.post(`/v3/lead/qualify`, body, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(`Failed to get lead qualifications`);
    }

    const data = await this.toJson(response, QualificationResponseSchema);
    return data;
  }

  /**
   * Submit a lead.
   * @param body - The lead request.
   * @param options - Optional request options.
   * @returns The lead submit response.
   */
  async submitLead(
    body: LeadRequest,
    options: APIRequestOptions = {},
  ): Promise<LeadSubmitResponse> {
    const response = await this.post(`/v3/lead`, body, options);

    if (this.hasError(response)) {
      console.error(`Failed to submit lead`);
    }

    const data = await this.toJson(response, LeadSubmitResponseSchema);
    return data;
  }

  /**
   * Submit a lead using the v4 integration-based endpoint.
   * @param body - The v4 lead request.
   * @param options - Optional request options.
   * @returns The lead submit response.
   */
  async submitLeadV4(
    body: LeadSubmitV4Request,
    options: APIRequestOptions = {},
  ): Promise<LeadSubmitResponse> {
    const response = await this.post(`/v4/lead`, body, options);

    if (this.hasError(response)) {
      console.error(`Failed to submit v4 lead`);
      const errorMessage = await this.buildHttpErrorMessage(
        response,
        'Error submitting v4 lead',
      );

      throw new Error(errorMessage);
    }

    const data = await this.toJson(response, LeadSubmitResponseSchema);
    return data;
  }

  /**
   * Submit a lead to the v5 vertical-neutral endpoint.
   * @param body - The v5 lead request.
   * @param options - Optional request options (e.g. an abort `signal`).
   * @returns The intake response; throws `LeadSubmitV5Error` on a non-2xx.
   */
  async submitLeadV5(
    body: LeadSubmitV5Request,
    options: APIRequestOptions = {},
  ): Promise<LeadSubmitV5Response> {
    const response = await this.post(`/v5/lead`, body, options);
    const data: unknown = await response.json().catch((error: unknown) => {
      // A body read cut off by the caller's signal is a timeout, not a bad body.
      if (options.signal?.aborted) {
        throw options.signal.reason ?? error;
      }
      return undefined;
    });

    if (!response.ok) {
      const parsed = LeadSubmitV5ErrorBodySchema.safeParse(data);
      const errorBody = parsed.success ? parsed.data : {};
      // LDS's error middleware puts the code in `message`; free text is dropped.
      const code = [errorBody.code, errorBody.message].find(
        (candidate) =>
          candidate !== undefined && LDS_ERROR_CODE.test(candidate),
      );
      throw new LeadSubmitV5Error(
        response.status,
        code,
        errorBody.errors ?? [],
      );
    }

    const parsed = LeadSubmitV5ResponseSchema.safeParse(data);
    if (!parsed.success) {
      throw new LeadSubmitV5Error(response.status, 'invalid_response', []);
    }
    return parsed.data;
  }

  async getDiffyV4(
    body: DiffyReqPayload,
    params: { organizationId: string; offeringUuid?: string },
    options: APIRequestOptions = {},
  ): Promise<DiffyResponse> {
    const response = await this.post(
      this.buildDiffyV4Path(params),
      body,
      options,
    );

    if (this.hasError(response)) {
      console.error(`Failed to get v4 diffy`);
      const errorMessage = await this.buildHttpErrorMessage(
        response,
        'Error getting v4 diffy',
      );

      throw new Error(errorMessage);
    }

    const data = await this.toJson(response, DiffyResponseSchema);
    return data;
  }

  /**
   * Validate a phone number.
   * @param body - The phone validation request payload.
   * @param options - Optional request options.
   * @returns The phone validation response.
   */
  async validatePhone(
    body: PhoneValidationReqPayload,
    options: APIRequestOptions = {},
  ): Promise<ValidatePhoneResponse> {
    const response = await this.post(`/v2/validate/phone`, body, options);

    if (this.hasError(response)) {
      console.error(`Failed to validate phone`);
    }

    const data = await this.toJson(response, ValidatePhoneResponseSchema);
    return data;
  }

  /**
   * Validate a zip code.
   * @param body - The zip validation request payload.
   * @param options - Optional request options.
   * @returns The zip validation response.
   */
  async validateZip(
    body: ZipValidationReqPayload,
    options: APIRequestOptions = {},
  ): Promise<ValidateZipResponse> {
    const response = await this.post(`/v2/validate/zip`, body, options);

    if (this.hasError(response)) {
      console.error(`Failed to validate zip`);
    }

    const data = await this.toJson(response, ValidateZipResponseSchema);
    return data;
  }

  /**
   * Get the diffy (difference) schema for a school and program.
   * @param body - The diffy request payload.
   * @param params - The school ID and program ID.
   * @param options - Optional request options.
   * @returns The diffy response.
   */
  async getDiffy(
    body: DiffyReqPayload,
    params: { schoolId: string; programId: string },
    options: APIRequestOptions = {},
  ): Promise<DiffyResponse> {
    const response = await this.post(
      `/v3/diffy/${params.schoolId}/${params.programId}`,
      body,
      options,
    );

    if (this.hasError(response)) {
      console.error(`Failed to get diffy`);
    }

    const data = await this.toJson(response, DiffyResponseSchema);
    return data;
  }

  private buildLeadSchemaV4Path(params: {
    organizationId: string;
    offeringUuid?: string;
  }): string {
    return this.buildOrganizationScopedV4Path('/v4/schema', params);
  }

  private buildDiffyV4Path(params: {
    organizationId: string;
    offeringUuid?: string;
  }): string {
    return this.buildOrganizationScopedV4Path('/v4/diffy', params);
  }

  private async buildHttpErrorMessage(
    response: Response,
    fallbackMessage: string,
  ): Promise<string> {
    const errorData = (await response.json().catch(() => ({}))) as Record<
      string,
      unknown
    >;

    const base =
      typeof errorData['error'] === 'string'
        ? errorData['error']
        : typeof errorData['message'] === 'string'
          ? (errorData['message'] as string)
          : `${fallbackMessage}: ${response.status}`;

    // Surface any structured validation detail beyond the bare code so callers
    // and logs can see *why* a request failed (e.g. a v4 `invalid_lead`
    // response carries the offending fields). Without this, the rejection code
    // alone (`invalid_lead`) gives no actionable signal.
    const detail: Record<string, unknown> = { ...errorData };
    delete detail['error'];
    delete detail['message'];

    if (Object.keys(detail).length > 0) {
      try {
        return `${base}: ${JSON.stringify(detail)}`;
      } catch {
        return base;
      }
    }

    return base;
  }

  private buildOrganizationScopedV4Path(
    basePath: string,
    params: { organizationId: string; offeringUuid?: string },
  ): string {
    const path = `${basePath}/${encodeURIComponent(params.organizationId)}`;

    if (params.offeringUuid) {
      return `${path}/${encodeURIComponent(params.offeringUuid)}`;
    }

    return path;
  }
}
